<template>
    <table class="tx-details-table">
        <tbody>
            <!--<div v-if="!txDetails" class="skeleton skeleton&#45;&#45;inline">from address1 to address2</div>-->
            <template v-if="isTxSimple && txDetails?.event === 'jetton_swap'">
                <tr>
                    <td class="tx-details-table__left">{{ $t('address.all_tx_table.from') }}</td>
                    <td><ui-address style="max-width: 210px" v-bind:address="txDetails.from" v-bind:alias="txDetails?.source_alias" v-bind:type="txDetails?.source_type" /></td>
                </tr>
                <tr>
                    <td class="tx-details-table__left">{{ $t('dex_table.dex') }}</td>
                    <td>
                        <a v-if="txDetails.dex === 'dedust'" href="https://dedust.io/" target="_blank">DeDust.io</a>
                        <span v-else-if="txDetails.dex === 'tonco'">TONCO</span>
                        <span v-else-if="txDetails.dex === 'stonfi' || txDetails.dex === 'stonfi_v1' || txDetails.dex === 'stonfi_v2'">StonFi</span>
                        <span v-else>{{ txDetails.dex }}</span>
                    </td>
                </tr>
                <tr>
                    <td class="tx-details-table__left">{{ $t('dex_table.rate') }}</td>
                    <td>
                        <span class="tx-swap-rate">
                            <span v-if="rateFlipped">1 {{ swapTickerIn }} &approx; {{ swapRateFlipped }} {{ swapTickerOut }}</span>
                            <span v-else>1 {{ swapTickerOut }} &approx; {{ swapRate }} {{ swapTickerIn }}</span>
                            <button class="tx-swap-rate__flip" type="button" v-on:click="rateFlipped = !rateFlipped">
                                <icon-arrow-left-right class="tx-swap-rate__flip-icon" />
                            </button>
                        </span>
                    </td>
                </tr>
            </template>
            <template v-else-if="isTxSimple">
                <tr>
                    <td class="tx-details-table__left">{{ $t('address.all_tx_table.from') }}</td>
                    <td>
                        <!-- Exception for nft purchase event, when sender is undefined -->
                        <div style="text-transform: capitalize" v-if="txDetails?.event === 'nft_purchase' && !txDetails.from && txDetails?.meta?.marketplace">
                            <ui-address v-if="txDetails.meta.marketplace === 'fragment'" address="UQBAjaOyi2wGWlk-EDkSabqqnF-MrrwMadnwqrurKpkla4QB" />
                            <span v-else>{{ txDetails.meta.marketplace }}</span>
                        </div>
                        <ui-address v-else style="max-width: 210px" v-bind:address="txDetails.from" v-bind:alias="txDetails?.source_alias" v-bind:type="txDetails?.source_type" />
                    </td>
                </tr>
                <tr>
                    <td class="tx-details-table__left">{{ $t('address.all_tx_table.to') }}</td>
                    <td><ui-address style="max-width: 210px" v-bind:address="txDetails.to" v-bind:alias="txDetails?.destination_alias" v-bind:type="txDetails?.destination_type" /></td>
                </tr>
            </template>
            <tr v-if="isTickTock">
                <td class="tx-details-table__left">{{ $t('dex_table.type') }}</td>
                <td>{{ $t('tx.service_tx') }}</td>
            </tr>
            <tr v-if="txDetails?.event === 'nft_purchase' && txDetails?.meta?.marketplace">
                <td class="tx-details-table__left">{{ $t('nft_sale.marketplace') }}</td>
                <td style="text-transform: capitalize">
                    <ui-address v-if="txDetails.meta.marketplace === 'fragment'" address="UQBAjaOyi2wGWlk-EDkSabqqnF-MrrwMadnwqrurKpkla4QB" />
                    <span v-else>{{ txDetails.meta.marketplace }}</span>
                </td>
            </tr>
            <tr>
                <td class="tx-details-table__left">{{ $t('tx.hash') }}</td>
                <td>
                    <div v-if="!hash" class="skeleton">Hash</div>
                    <ui-copy-button v-else v-bind:copy="hash">
                        {{ shortenHash }}
                    </ui-copy-button>
                </td>
            </tr>
            <tr>
                <td class="tx-details-table__left">{{ $t('common.date') }}</td>
                <td>
                    <div v-if="!date" class="skeleton" style="width: 140px">Date</div>
                    <div v-else>
                        {{ date }}
                        <span v-if="duration > 0" class="muted">— {{ $t('tx.in_duration') }} <ui-date-period v-bind:seconds="duration" /></span>
                        <span v-else class="muted">— {{ $t('tx.less_than_sec') }}</span>
                    </div>
                </td>
            </tr>
        </tbody>
    </table>
</template>

<script>
import { truncateTicker } from '~/helpers';
import IconArrowLeftRight from '@img/icons/lucide/arrow-left-right.svg?inline';

export default {
    components: { IconArrowLeftRight },
    props: ['traceActions', 'date', 'hash', 'isTxSimple', 'isTickTock', 'duration'],
    data() {
        return {
            rateFlipped: false,
        };
    },
    computed: {
        swapTickerOut() {
            if (this.txDetails.meta.jetton_out_address === 'EQCM3B12QK1e4yZSf8GtBRT0aLMNyEsBc_DhVfRRtOEffLez') {
                return 'GRAM';
            }
            return truncateTicker(this.txDetails.meta.symbol_out) || 'GRAM';
        },
        swapTickerIn() {
            if (this.txDetails.meta.jetton_in_address === 'EQCM3B12QK1e4yZSf8GtBRT0aLMNyEsBc_DhVfRRtOEffLez') {
                return 'GRAM';
            }
            return truncateTicker(this.txDetails.meta.symbol_in) || 'GRAM';
        },
        swapAmounts() {
            const decimalsIn = Number.isInteger(this.txDetails.meta.decimals_in) ? this.txDetails.meta.decimals_in : 9;
            const decimalsOut = Number.isInteger(this.txDetails.meta.decimals_out) ? this.txDetails.meta.decimals_out : 9;

            return {
                amountIn: this.txDetails.meta.amount_in / 10 ** decimalsIn,
                amountOut: this.txDetails.meta.amount_out / 10 ** decimalsOut,
            };
        },
        swapRate() {
            return (this.swapAmounts.amountIn / this.swapAmounts.amountOut).toFixed(6);
        },
        swapRateFlipped() {
            return (this.swapAmounts.amountOut / this.swapAmounts.amountIn).toFixed(6);
        },
        txDetails() {
            if (!this.traceActions) return null;
            // const actionsAmount = this.traceActions.length;

            if (this.traceActions[0]?.simple_preview) {
                return this.traceActions[0]?.simple_preview;
            }
            return this.traceActions[0];
        },
        shortenHash() {
            return `${this.hash.slice(0, 10)}...${this.hash.slice(-6)}`;
        },
    },
};
</script>

<style lang="scss">
.tx-details-table {
    margin-top: 8px;
    border-radius: 8px;
    overflow: hidden;
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    border: 1px solid var(--card-border-color);

    td {
        white-space: nowrap;
        padding: 8px 12px;
        border-bottom: 1px solid var(--card-border-color);
    }

    tr:last-child {
        td {
            border-bottom: none;
        }
    }

    &__left {
        text-transform: capitalize;
        background: var(--code-viewer-tab-inactive-background);
        border-right: 1px solid var(--card-border-color);
    }
}

.tx-swap-rate {
    display: inline-flex;
    align-items: center;
    gap: 6px;

    &__flip {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 2px;
        margin: -2px 0;
        border: none;
        border-radius: 4px;
        background: transparent;
        color: var(--body-muted-text-color);
        cursor: pointer;
        transition: color .15s;

        &:hover {
            color: var(--body-text-color);
        }
    }

    &__flip-icon {
        width: 14px;
        height: 14px;
    }
}

.mobile-tx-address-list {
    width: 100%;
    text-align: center;
    padding: 6px 0 0 0;
    p {
        margin: 8px 0;
        padding: 0;
    }
}
</style>
